import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { FcAddRow, FcSynchronize } from 'react-icons/fc';
import Swal from 'sweetalert2';
import Layout from './components/Layout';
import Button from './components/Button';
import { AlertWarning } from './components/Alert';
import Loading from './components/Loading';
import apiClient from './apiClient';
import { verifyPin } from './utils/verifyPin';
import { useNavigate } from 'react-router-dom';

const thaiMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

function formatThaiDate(dateStr: string) {
    if (!dateStr || dateStr === '0000-00-00') return '-'
    const [y, m, d] = dateStr.split('-')
    return `${Number(d)} ${thaiMonths[Number(m) - 1]} ${Number(y) + 543}`
}

function Home() {
    const [isLoading, setLoading] = useState(false)
    const [search_license, setSearchLicense] = useState('')
    const [search_car_number, setSearchCarNumber] = useState('')
    const [search_status, setSearchStatus] = useState('unsold')
    const [cars, setCars] = useState<any>([])
    const navigate = useNavigate()

    useEffect(() => { loadCars() }, [])

    function loadCars() {
        setLoading(true)
        apiClient.post('/v1/car_search', { search_license: '', search_car_number: '', search_status })
            .then(res => setCars(res.data.cars || []))
            .catch(error => console.log('error:', error))
            .finally(() => setLoading(false))
    }

    function searchCar() {
        setLoading(true)
        apiClient.post('/v1/car_search', { search_license, search_car_number, search_status })
            .then(res => {
                const result = res.data.cars
                if (result.length === 0) AlertWarning('ไม่พบข้อมูลรถ')
                setCars(result)
            })
            .catch(error => console.log('error:', error))
            .finally(() => setLoading(false))
    }

    function clearSearch() {
        setSearchLicense('')
        setSearchCarNumber('')
        setSearchStatus('unsold')
        loadCars()
    }

    async function deleteCar(carId: number, carName: string, carLicense: string) {
        const ok = await verifyPin('car_delete', `ลบรถ ${carName} ${carLicense}`)
        if (!ok) return
        Swal.fire({
            title: 'ยืนยันการลบ',
            text: `ต้องการลบ ${carName} ${carLicense} หรือไม่?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
            confirmButtonColor: '#d33',
        }).then((result) => {
            if (result.isConfirmed) {
                apiClient.delete('/v1/car/' + carId)
                    .then(() => loadCars())
                    .catch(error => console.log('error:', error))
            }
        })
    }

    const totalProfit = cars.reduce((sum: number, c: any) => sum + Number(c.net_profit || 0), 0)

    return (
        <Layout>
            <Loading isLoading={isLoading} />
            <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6'>

            {/* Search Bar */}
            <div className='bg-rose-100/50 border border-rose-200 rounded-lg p-3 mb-4'>
                <div className='flex flex-wrap items-center gap-2 justify-center'>
                    <input
                        type='text'
                        placeholder='ค้นหาตามเลขตัวถัง'
                        value={search_car_number}
                        onChange={(e) => setSearchCarNumber(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') searchCar() }}
                        className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto sm:flex-1 max-w-[200px]'
                    />
                    <input
                        type='text'
                        placeholder='ค้นหาตามป้ายทะเบียน'
                        value={search_license}
                        onChange={(e) => setSearchLicense(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') searchCar() }}
                        className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto sm:flex-1 max-w-[200px]'
                    />
                    <select
                        value={search_status}
                        onChange={(e) => setSearchStatus(e.target.value)}
                        className='px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-auto max-w-[150px]'
                    >
                        <option value='unsold'>ยังไม่ขาย</option>
                        <option value='sold'>ขายแล้ว</option>
                        <option value='all'>ทั้งหมด</option>
                    </select>
                    <div className='flex gap-1.5 w-full sm:w-auto justify-end'>
                        <Button variant='dark' size='md' onClick={searchCar}>
                            ค้นหา
                        </Button>
                        <Button variant='neutral' size='sm' className='px-2 py-2' onClick={clearSearch}>
                            <FcSynchronize size='20' />
                        </Button>
                        <Button variant='success' size='md' icon={<FcAddRow size='18' />} onClick={() => navigate('/newcar')}>
                            เพิ่มรถใหม่
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                    <thead>
                        <tr className='bg-gray-100 border-b border-gray-200'>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700 w-10'>#</th>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700'>ยี่ห้อ / รุ่น</th>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700'>สี/เกียร์</th>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700'>ทะเบียน</th>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700'>วันที่ซื้อ</th>
                            <th className='px-3 py-2.5 text-left font-semibold text-gray-700'>วันที่ขาย</th>
                            <th className='px-3 py-2.5 text-right font-semibold text-gray-700 w-24'>ซื้อเข้า</th>
                            <th className='px-3 py-2.5 text-right font-semibold text-gray-700 w-24'>ขายออก</th>
                            <th className='px-3 py-2.5 text-right font-semibold text-gray-700 w-24'>กำไร</th>
                            <th className='px-3 py-2.5 text-center font-semibold text-gray-700 w-28'>จัดการ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cars.length === 0 ? (
                            <tr>
                                <td colSpan={10} className='text-center text-gray-400 py-8'>
                                    {isLoading ? 'กำลังโหลด...' : 'ไม่พบข้อมูลรถ'}
                                </td>
                            </tr>
                        ) : cars.map((car: any, index: number) => (
                            <tr key={car.car_id} className='border-b border-gray-100 hover:bg-gray-50'>
                                <td className='px-3 py-2.5'>{index + 1}</td>
                                <td className='px-3 py-2.5 font-medium text-gray-900'>{car.car_brand} {car.car_model_year}</td>
                                <td className='px-3 py-2.5'>{car.car_gear_color}</td>
                                <td className='px-3 py-2.5'>{car.car_license_plate}</td>
                                <td className='px-3 py-2.5'>{formatThaiDate(car.car_date_in)}</td>
                                <td className='px-3 py-2.5'>
                                    {!car.car_date_sell || car.car_date_sell === '0000-00-00' ? (
                                        <span className='text-green-600 font-semibold'>ยังไม่ขาย</span>
                                    ) : formatThaiDate(car.car_date_sell)}
                                </td>
                                <td className='px-3 py-2.5 text-right'>
                                    <NumericFormat value={car.expense_buy_in} displayType='text' thousandSeparator=',' decimalScale={2} />
                                </td>
                                <td className='px-3 py-2.5 text-right'>
                                    <NumericFormat value={car.income_price} displayType='text' thousandSeparator=',' decimalScale={2} />
                                </td>
                                <td className={`px-3 py-2.5 text-right font-semibold ${car.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <NumericFormat value={car.net_profit} displayType='text' thousandSeparator=',' decimalScale={2} />
                                </td>
                                <td className='px-3 py-2.5 text-center'>
                                    <div className='flex gap-1 justify-center'>
                                        <Button variant='primary' size='sm' className='w-[52px]' onClick={() => navigate('/newcar/' + car.car_id)}>
                                            แก้ไข
                                        </Button>
                                        <Button variant='danger' size='sm' className='w-[52px]' onClick={() => deleteCar(car.car_id, `${car.car_brand} ${car.car_model_year}`, car.car_license_plate)}>
                                            ลบ
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            {cars.length > 0 && (
                <div className='flex justify-end gap-4 mt-2 mb-4 text-sm text-gray-700'>
                    <span>ทั้งหมด: <strong>{cars.length}</strong> คัน</span>
                    <span>รวมกำไร: <strong className={totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                        <NumericFormat value={totalProfit} displayType='text' thousandSeparator=',' decimalScale={2} />
                    </strong></span>
                </div>
            )}
            </div>
        </Layout>
    )
}

export default Home;
